import { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { MessageCircleQuestion, Search, BookOpen, CheckCircle, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const BookQuestionsPage = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBook, setFilterBook] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoryBooks, setCategoryBooks] = useState([]);
  const [expandedQ, setExpandedQ] = useState(null);

  useEffect(() => {
    api.get('/course-categories').then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  // Load books when category changes
  useEffect(() => {
    setFilterBook('');
    setCategoryBooks([]);
    if (filterCategory) {
      api.get(`/category-books?category=${encodeURIComponent(filterCategory)}`).then(r => setCategoryBooks(r.data || [])).catch(() => {});
    }
  }, [filterCategory]);

  // Load questions when filters change
  useEffect(() => {
    loadQuestions();
  }, [filterCategory, filterBook]); // eslint-disable-line

  const loadQuestions = async () => {
    setLoading(true);
    try {
      let url = '/questions/all?';
      if (filterCategory) url += `category=${encodeURIComponent(filterCategory)}&`;
      if (filterBook) url += `book_key=${filterBook}&`;
      const res = await api.get(url);
      setQuestions(res.data || []);
    } catch (e) { console.error('Failed to load book questions:', e); }
    finally { setLoading(false); }
  };

  const filtered = questions.filter(q => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (q.question || '').toLowerCase().includes(s)
      || (q.answer || '').toLowerCase().includes(s)
      || (q.selected_text || '').toLowerCase().includes(s)
      || (q.book_title || '').toLowerCase().includes(s);
  });

  return (
    <div className="max-w-4xl mx-auto space-y-6" dir="rtl" data-testid="book-questions-page">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #C19A6B, #8B6F47)' }}>
          <MessageCircleQuestion className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-amber-900">أسئلة المساقات</h1>
          <p className="text-sm text-gray-500">الأسئلة المجابة من المختصين</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex items-center gap-2 flex-1">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="بحث في الأسئلة والإجابات..."
              className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
              data-testid="search-questions" />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
            data-testid="filter-category">
            <option value="">كل التصنيفات</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterBook} onChange={e => setFilterBook(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
            data-testid="filter-book">
            <option value="">كل الكتب</option>
            {categoryBooks.map(b => <option key={b.book_key} value={b.book_key}>{b.title}</option>)}
          </select>
        </div>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">جارٍ التحميل...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageCircleQuestion className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">لا توجد أسئلة مجابة</p>
          <p className="text-gray-400 text-sm mt-1">يمكنك طرح أسئلتك أثناء قراءة الكتب</p>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-gray-500">{filtered.length} سؤال مجاب</p>
          {filtered.map(q => (
            <Card key={q.id}
              className={`overflow-hidden cursor-pointer transition-all hover:shadow-md ${expandedQ === q.id ? 'ring-2 ring-amber-300' : ''}`}
              onClick={() => setExpandedQ(expandedQ === q.id ? null : q.id)}
              data-testid={`question-card-${q.id}`}>
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 leading-relaxed">{q.question}</p>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                        <BookOpen className="w-3 h-3" /> {q.book_title}
                      </span>
                      <span className="text-xs text-gray-400">صفحة {q.page_num}</span>
                      <span className="text-xs text-gray-400">سؤال أحد القراء</span>
                    </div>
                  </div>
                  <ChevronLeft className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${expandedQ === q.id ? 'rotate-90' : ''}`} />
                </div>
                {expandedQ === q.id && (
                  <div className="mt-4 space-y-3 pr-11">
                    {q.selected_text && (
                      <div className="bg-amber-50 rounded-lg p-3 border-r-4 border-amber-300">
                        <p className="text-xs text-amber-600 mb-1">النص المرتبط:</p>
                        <p className="text-sm text-amber-900">{q.selected_text}</p>
                      </div>
                    )}
                    <div className="bg-emerald-50/50 rounded-lg p-3 border-r-4 border-emerald-300">
                      <p className="text-xs text-emerald-700 mb-1 font-bold">الإجابة:</p>
                      <div
                        className="text-sm text-emerald-900 leading-relaxed answer-html prose prose-sm max-w-none"
                        dir="rtl"
                        dangerouslySetInnerHTML={{ __html: q.answer }}
                      />
                      {Array.isArray(q.answer_refs) && q.answer_refs.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-emerald-200">
                          <p className="text-xs font-bold text-emerald-700 mb-1">المراجع:</p>
                          <ol className="text-xs text-emerald-900 list-decimal pr-5 space-y-0.5" dir="rtl">
                            {q.answer_refs.map((r, i) => <li key={i}>{r}</li>)}
                          </ol>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/courses/${q.course_id}/read/${q.book_key}?page=${q.page_num}`); }}
                      className="text-xs text-amber-700 hover:text-amber-900 font-medium underline"
                      data-testid={`go-to-book-${q.id}`}>
                      فتح الكتاب في هذه الصفحة
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BookQuestionsPage;
